/**
 * ReportExportModal — Salesforce-style export dialog for reports.
 *
 * Lets the user pick a format (PDF / CSV / Excel) and triggers the
 * existing wedge-5 backend export endpoint
 * (`POST /v1/reports/:id/export?format=...`) which:
 *   - Runs the engine against the latest CRM data
 *   - Honours filters, sorting, grouping, summaries, charts
 *   - Gates on wedge-3 sharing visibility (READ on the report)
 *   - Returns a binary stream of the rendered file
 *
 * The exporter on the server already produces CSV (records + grand
 * totals), Excel (styled header + auto-fit columns + grand totals),
 * and PDF (landscape with banded rows + summary header). So this modal
 * is the thin presentation layer that:
 *   1. Replaces the broken "PDF export is queued" placeholder toast.
 *   2. Adds loading + error UX.
 *   3. Disables duplicate clicks while a format is in flight.
 */
import React, { useCallback, useState } from 'react';
import {
  FileText, FileSpreadsheet, FileType, Loader2, X, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { exportsApi } from '../api/exportsApi';

// Format catalog — single source of truth for cards + dispatch.
// `bytes` keeps the runtime export-size hint visible to the user so they
// know roughly what to expect before clicking; `description` is the
// "what does this do" line under each card.
const FORMATS = [
  {
    key: 'pdf',
    label: 'PDF',
    icon: FileType,
    accent: 'red',
    description: 'Print-ready report with title, filters, charts, and grouped tables.',
    detail: 'Best for sharing or archiving. Landscape layout with bundled chart visualisations.',
  },
  {
    key: 'csv',
    label: 'CSV',
    icon: FileText,
    accent: 'emerald',
    description: 'Raw rows for spreadsheets or BI tools.',
    detail: 'Honours the report’s selected columns and current sort/filter.',
  },
  {
    key: 'excel',
    label: 'Excel',
    icon: FileSpreadsheet,
    accent: 'green',
    description: 'Styled .xlsx with formatted headers and grand totals.',
    detail: 'Preserves grouped section labels where possible.',
  },
];

const ACCENT_CLS = {
  red: {
    border: 'border-red-200', icon: 'text-red-600 bg-red-50',
    selected: 'border-red-400 ring-1 ring-red-200',
  },
  emerald: {
    border: 'border-emerald-200', icon: 'text-emerald-600 bg-emerald-50',
    selected: 'border-emerald-400 ring-1 ring-emerald-200',
  },
  green: {
    border: 'border-green-200', icon: 'text-green-600 bg-green-50',
    selected: 'border-green-400 ring-1 ring-green-200',
  },
};

const ReportExportModal = ({ open, onClose, report }) => {
  // `busy` carries the format key currently exporting; null = idle.
  // Both the cards' click handlers and the close button consult it to
  // prevent duplicate dispatches.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Resetting transient state on each open so a re-opened modal starts clean.
  const handleOpenChange = (v) => {
    if (!v && !busy) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const handleExport = useCallback(async (formatKey) => {
    if (!report?.id) {
      setError('Save the report before exporting.');
      return;
    }
    if (busy) return; // dedupe
    setBusy(formatKey);
    setError(null);
    setSuccess(null);
    try {
      const size = await exportsApi.exportReport(report, formatKey);
      const sizeKb = size ? Math.max(1, Math.round(size / 1024)) : null;
      setSuccess({
        format: formatKey.toUpperCase(),
        sizeKb,
      });
      toast.success(
        `${formatKey.toUpperCase()} export ready${sizeKb ? ` (${sizeKb} KB)` : ''}`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || `Failed to export ${formatKey.toUpperCase()}`;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  }, [report, busy]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="text-lg text-slate-900 flex items-center gap-2 truncate">
            Export <span className="font-normal text-slate-500 truncate">"{report?.name || 'Report'}"</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Generated server-side from the latest CRM data, honouring filters, sorting, and your sharing permissions.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-2.5">
          {FORMATS.map(({ key, label, icon: Icon, accent, description, detail }) => {
            const cls = ACCENT_CLS[accent] || ACCENT_CLS.red;
            const isBusyHere = busy === key;
            const anyBusy = !!busy;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleExport(key)}
                disabled={anyBusy}
                className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl border bg-white transition-all hover:border-slate-300 ${cls.border} ${
                  anyBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'
                }`}
              >
                <div className={`p-2.5 rounded-lg flex-shrink-0 ${cls.icon}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{label}</span>
                    {isBusyHere && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{description}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{detail}</div>
                </div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide">
                  {isBusyHere ? 'Generating…' : `.${key === 'excel' ? 'xlsx' : key}`}
                </div>
              </button>
            );
          })}
        </div>

        {(error || success) && (
          <div className="px-6 pb-4">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200">
                <AlertTriangle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-rose-700 flex-1">{error}</div>
                <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {success && !error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <div className="text-xs text-emerald-800">
                  {success.format} exported{success.sizeKb ? ` (${success.sizeKb} KB)` : ''} — your browser saved the file.
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">
            Reports are generated on demand; large datasets may take a few seconds.
          </span>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={!!busy}>
            {busy ? 'Working…' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportExportModal;
